import { useEffect, useState, type FC } from 'react'

import { Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getWhiteLabelForDealerStyle, useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { demoCustomersResources, type DemoCustomers } from '@/api/demoCustomersApi'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { DemoCustomerTabs } from '@/components/sales-support/DemoCustomerTabs'
import { useSearchParamsByKey } from '@/hooks/searchParams'

import { EmployeesTable } from './EmployeesTable'
import { UsersTable } from './UsersTable'

const STATUS_ID = {
  USERS: 1,
  EMPLOYEES: 2,
}

const STATUS_FILTER_OPTIONS = [
  { value: STATUS_ID.USERS, label: <Translation id="Users" /> },
  { value: STATUS_ID.EMPLOYEES, label: <Translation id="Employees" /> },
]

export const DemoCustomerInfo: FC<{ demoCustomer: DemoCustomers }> = ({ demoCustomer }) => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()

  const [queryStatus, setQueryStatus] = useSearchParamsByKey('status')
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')

  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [statusFilter, setStatusFilter] = useState(Number(queryStatus) || STATUS_ID.USERS)

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  const { data: demoTargets, isLoading: isDemoTargetsLoading } = useQuery(
    queries.demoTargets(),
    demoCustomersResources.getDemoTargets
  )

  useEffect(() => {
    setSearchFilter(querySearch || '')
    setStatusFilter(Number(queryStatus) || STATUS_ID.USERS)
  }, [querySearch, queryStatus])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const handleManagerOpen = () => {
    demoTargets && window.open(demoTargets.managerUrl, '_blank', 'noopener,noreferrer')
  }
  const handleAppOpen = () => {
    demoTargets && window.open(demoTargets.appUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Container className="px-0">
      <div>
        <div className="mx-0 my-4 min-w-60">
          <DemoCustomerTabs
            className="my-0 w-full"
            selectedValue={statusFilter}
            tabs={STATUS_FILTER_OPTIONS}
            onClick={statusId => {
              setStatusFilter(Number(statusId))
              setQueryStatus(statusId)
            }}
          />
        </div>
        <div className="flex flex-col items-stretch justify-between sm:flex-row sm:items-center">
          <Input
            id={`demo-customer-details-search-input${statusFilter}`}
            value={searchFilter}
            placeholder={translate('Search')}
            className="mobile:w-auto w-full"
            onChange={e => setSearchFilter(e.target.value)}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          />
          <Button
            className="mx-0 my-3 sm:my-0"
            variant="primary"
            disabled={isDemoTargetsLoading}
            onClick={() =>
              statusFilter === STATUS_ID.USERS ? handleManagerOpen() : handleAppOpen()
            }
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="Go to" className="mr-1" />
            {statusFilter === STATUS_ID.USERS ? 'Manager' : 'App'}
          </Button>
        </div>
      </div>

      {statusFilter === STATUS_ID.USERS && (
        <UsersTable searchFilter={searchFilter} users={demoCustomer?.users} />
      )}
      {statusFilter === STATUS_ID.EMPLOYEES && (
        <EmployeesTable searchFilter={searchFilter} employees={demoCustomer?.costObjects} />
      )}
    </Container>
  )
}
